import type React from 'react';

import { Box, useColorModeValue } from '@ecoinvent/ui';

import { FlowDetailSuppliers } from 'hooks/data/datasets/V4/flows/types';
import { formatNumericalValue } from 'utilities/numbers';

import { LAYOUT_CONFIG } from './constants';
import { generatePath, getTruncatedText } from './helpers';
import type { SourceNode } from './types';

type SankeyCanvasProps = {
  sourceNodes: SourceNode[];
  targetNode: { x: number; y: number; width: number };
  svgWidth: number;
  svgHeight: number;
  zoom: number;
  pan: { x: number; y: number };
  isDragging: boolean;
  hoveredNode: string | null;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onNodeHover: (sourceId: string) => void;
  onNodeLeave: () => void;
  onNodeClick: (source: FlowDetailSuppliers) => void;
  getNodeColor: (type: FlowDetailSuppliers['type']) => string;
  svgRef: React.RefObject<SVGSVGElement>;
};

export const SankeyCanvas = ({
  sourceNodes,
  targetNode,
  svgWidth,
  svgHeight,
  zoom,
  pan,
  isDragging,
  hoveredNode,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onMouseLeave,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onNodeHover,
  onNodeLeave,
  onNodeClick,
  getNodeColor,
  svgRef,
}: SankeyCanvasProps) => {
  const textColor = useColorModeValue('gray.800', 'white');
  const labelBgColor = useColorModeValue('white', 'rgba(255, 255, 255, 0.08)');
  const amountTextColor = useColorModeValue('gray.500', 'rgba(255, 255, 255, 0.92)');
  const linkColor = useColorModeValue('#38A169', '#9AE6B4');
  const sourceCount = sourceNodes.length;

  const getDimmedOpacity = (id: string) => (hoveredNode && hoveredNode !== id ? 0.2 : 1);

  return (
    <Box
      flex={1}
      position="relative"
      overflow="hidden"
      cursor={isDragging ? 'grabbing' : 'grab'}
      userSelect="none"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        style={{
          overflow: 'visible',
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: 'center center',
          transition: isDragging ? 'none' : 'transform 0.3s ease',
        }}
      >
        <defs>
          <filter id="sankey-card-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" />
            <feOffset dx="0" dy="1" result="offsetblur" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.15" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {(() => {
          const targetFlowYPositions: number[] = [];
          let cumY = targetNode.y;
          for (const s of sourceNodes) {
            targetFlowYPositions.push(cumY);
            cumY += s.height;
          }

          return sourceNodes.map((source, index) => {
            const cumulativeY = targetFlowYPositions[index];
            const targetFlowHeight = source.height;

            return (
              <g
                key={`flow-group-${source.id}`}
                opacity={getDimmedOpacity(source.id)}
                style={{ transition: 'opacity 0.3s ease' }}
              >
                <path
                  d={generatePath(source, targetNode.x, cumulativeY, targetFlowHeight)}
                  fill={source.color}
                  fillOpacity={hoveredNode === source.id ? 0.9 : 0.7}
                  style={{
                    transition: 'fill-opacity 0.3s ease',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                  }}
                  onMouseEnter={() => onNodeHover(source.id)}
                  onMouseLeave={onNodeLeave}
                  onClick={(e) => {
                    e.stopPropagation();
                    onNodeClick(source);
                  }}
                />
                <rect
                  x={targetNode.x}
                  y={cumulativeY}
                  width={LAYOUT_CONFIG.nodeWidth}
                  height={targetFlowHeight}
                  fill={getNodeColor(source.type)}
                  style={{
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                  }}
                  onMouseEnter={() => onNodeHover(source.id)}
                  onMouseLeave={onNodeLeave}
                  onClick={(e) => {
                    e.stopPropagation();
                    onNodeClick(source);
                  }}
                />
              </g>
            );
          });
        })()}

        {sourceCount <= LAYOUT_CONFIG.thresholds.maxLabels &&
          sourceNodes.map((source) => {
            const labelText = getTruncatedText(source.name, sourceCount);
            const amountText = `${formatNumericalValue(source.amount)} ${source.unit}`;

            const charWidth =
              sourceCount > LAYOUT_CONFIG.thresholds.medium
                ? LAYOUT_CONFIG.labelCard.charWidthLarge
                : LAYOUT_CONFIG.labelCard.charWidthSmall;
            const calculatedWidth = Math.max(labelText.length * charWidth, amountText.length * charWidth);
            const maxLabelWidth = Math.max(calculatedWidth, LAYOUT_CONFIG.labelCard.minWidth);
            const leftPadding =
              calculatedWidth > LAYOUT_CONFIG.labelCard.minWidth
                ? LAYOUT_CONFIG.labelCard.leftPaddingLarge
                : LAYOUT_CONFIG.labelCard.leftPaddingSmall;
            const rightPadding = LAYOUT_CONFIG.labelCard.rightPadding;
            const bgWidth = maxLabelWidth + leftPadding + rightPadding;
            const bgHeight =
              sourceCount > LAYOUT_CONFIG.thresholds.medium
                ? LAYOUT_CONFIG.labelCard.heightSmall
                : LAYOUT_CONFIG.labelCard.heightLarge;

            const isHovered = hoveredNode === source.id;

            return (
              <g
                key={`label-group-${source.id}`}
                opacity={getDimmedOpacity(source.id)}
                style={{ transition: 'opacity 0.3s ease', cursor: 'pointer' }}
                onMouseEnter={() => onNodeHover(source.id)}
                onMouseLeave={onNodeLeave}
                onClick={(e) => {
                  e.stopPropagation();
                  onNodeClick(source);
                }}
              >
                <rect
                  x={source.x - bgWidth - LAYOUT_CONFIG.labelCard.offsetX}
                  y={source.y + source.height / 2 - bgHeight / 2}
                  width={bgWidth}
                  height={bgHeight}
                  fill={labelBgColor}
                  rx="4"
                  filter="url(#sankey-card-shadow)"
                />
                <text
                  x={source.x - LAYOUT_CONFIG.nodeWidth - rightPadding}
                  y={source.y + source.height / 2 - 4}
                  textAnchor="end"
                  fontSize={sourceCount > LAYOUT_CONFIG.thresholds.medium ? '10' : '11'}
                  fill={isHovered ? linkColor : textColor}
                  style={{
                    fontWeight: '400',
                    textDecoration: isHovered ? 'underline' : 'none',
                  }}
                >
                  {labelText}
                </text>
                <text
                  x={source.x - LAYOUT_CONFIG.nodeWidth - rightPadding}
                  y={source.y + source.height / 2 + (sourceCount > LAYOUT_CONFIG.thresholds.medium ? 8 : 9)}
                  textAnchor="end"
                  fontSize={sourceCount > LAYOUT_CONFIG.thresholds.medium ? '9' : '10'}
                  fill={amountTextColor}
                  fontWeight="600"
                >
                  {amountText}
                </text>
              </g>
            );
          })}

        {sourceNodes.map((source) => (
          <rect
            key={`source-${source.id}`}
            x={source.x}
            y={source.y}
            width={source.width}
            height={source.height}
            fill={getNodeColor(source.type)}
            opacity={getDimmedOpacity(source.id)}
            style={{
              transition: 'opacity 0.3s ease',
              cursor: 'pointer',
            }}
            onMouseEnter={() => onNodeHover(source.id)}
            onMouseLeave={onNodeLeave}
            onClick={(e) => {
              e.stopPropagation();
              onNodeClick(source);
            }}
          />
        ))}
      </svg>
    </Box>
  );
};
